// src/com/trans_plan_bind/TransPlanBindManager.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, CircularProgress,
    TableContainer, Table, TableBody, TableRow, TableCell, Tooltip, IconButton, TableHead,
    Chip
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import ForwardIcon from '@mui/icons-material/Forward';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import BuildIcon from '@mui/icons-material/Build';
import AddHomeIcon from '@mui/icons-material/AddHome'; // 동적 그룹에 추가. 또는 단독운송을 동적으로 묶기
import UndoIcon from '@mui/icons-material/Undo';
import NorthWestIcon from '@mui/icons-material/NorthWest'; // 단독을 이 위에 추가
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // 단독을 맨 끝에 추가.
import ReplyIcon from '@mui/icons-material/Reply'; // close
import { AppWord, AppObject, AppStyle, TopCode } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import StoreHouseSvg from '../../asset/image/store_house.svg';
import {
    authTableTreeRepo, transPlanBindStoreListRepo, transPlanBindStoreNotBoundListRepo, userInfoRepo
} from '../../value/AppModel';
import FlexyTable from '../../common/flexytable/FlexyTable';
import {
    ADD_TRANS_PLAN_BIND_STORE, EDIT_TRANS_PLAN_BIND_STORE, LIST_TRANS_PLAN_BIND_STORE_GROUP_AND_SOLO, REMOVE_TRANS_PLAN_BIND_STORE, transPlanBindStoreListToTree
} from '../trans_plan_bind_store/TransPlanBindStoreGql';
import { TransPlanBindStoreDictionary } from '../trans_plan_bind_store/TransPlanBindStoreDict';
import { GET_TRANS_PLAN_TSR } from '../trans_plan_tsr/TransPlanTsrGql';
import { ControlBox, CountLabel, ToolBox } from '../../common/TopViewControl';
import SearchBox from '../../common/SearchBox';
import VzmCheckbox from '../../common/VzmCheckbox';
import { ADD_TRANS_PLAN_BIND } from './TransPlanBindGql';
import { enqueueSnackbar } from 'notistack';

const BlinkingIconButton = styled(IconButton)(({ theme }) => ({
    '@keyframes blink': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.4 },
        '100%': { opacity: 1 }
    },
    animation: 'blink 0.3s linear infinite',
}));

const BigContainer = styled(Box)({display:'flex', height:'100%', minWidth: '80vw'});
const PlanAndSoloList = styled(Box)({
    flexGrow:1, display:'flex', flexDirection:'column', marginRight: 5
});
const BindArea = styled(Box)({
    flexGrow:1, display: 'flex', flexDirection: 'column', marginLeft: 5
});

const PlanBox = styled(Box)({
    border: AppStyle.BorderCCC, borderRadius: 5, marginBottom: 10, minWidth: 460
});
const PlanBindBox = styled(Box)({
    border: AppStyle.BorderCCC, borderRadius: 5, marginBottom: 10, minWidth: 400
});

const PlanBoxHeader = styled(Box)({
    paddingLeft: 10, backgroundColor: AppStyle.PrimaryRGB, color: 'white', height: 40,
    display: 'flex', alignItems: 'center', juisityItems: 'center',
    borderTopLeftRadius: 5, borderTopRightRadius: 5
});

const PlanBoxBody = styled(Box)({
    padding: 10, minHeight: 110, boxSizing: 'border-box', fontSize: '0.95rem'
});


const TitleCell = styled(TableCell)({ fontSize: '0.8rem', color: '#66f', padding: 5,});
const ValueCell = styled(TableCell)({ padding: 5 });


const SoloListContainer = styled(Box)({
    flexGrow:1, display: 'flex', flexDirection: 'column'
});

const ListHeader = styled(Box)({
    border: AppStyle.BorderCCC, borderRadius: 5, marginBottom: 5, height: 40, padding: '0 10px',
    display: 'flex', alignItems: 'center', fontSize: '0.9rem', backgroundColor: AppStyle.SuccessRGB, color: 'white',
});

const ListControl = styled(Box)({padding:'0 5px 5px 5px', display:'flex', alignItems:'center'});

const BindTreeContainer = styled(Box)({ flexGrow:1, display:'flex', flexDirection:'column' });

const BindTreeBox = styled(Box)({
    flexGrow: 1, border: AppStyle.BorderCCC, borderRadius: 5, position:'relative'
});

const BindTreeWrapper = styled(Box)({
    padding: 10, position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto'
});

const BoundGroupRecord = styled(Box)({
    marginTop: 2, borderTop: AppStyle.BorderCCC,
    '&:first-of-type': {marginTop: 0, borderTop: 'none'},
});

const BoundGroupVehicle = styled(Box)({
    padding: 5, display: 'flex', alignItems: 'center', fontSize: '0.9rem'
});

const BoundGroupStoreListBox = styled(Box)({
    marginLeft: 20
});

const BoundStoreRow = styled(Box)({
    borderTop: AppStyle.BorderCCC, display: 'flex', alignItems: 'center', fontSize: '0.85rem', padding:5,
    cursor: 'pointer',
    '&:first-of-type': {borderTop: 'none'},
    '&:hover': {backgroundColor: '#eef'}
});

const BoundStoreName = styled(Box)({flexGrow: 1});
const BoundStoreHocha = styled(Box)({width:50});
const BoundStoreDptrReqTime = styled(Box)({width:95});
/* const BoundStoreTools = styled('div')({
    display:'flex', justifyContent:'flex-end', alignItems:'center', width: 60
}); */

const getHhmm = (value) => {
    return value ? value.substring(11,16) : null
};

function isBindable(store, plan) {
    // 이미 TsrIssued면 불가능.
    if(plan.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) return false;
    if(store.storeId === plan.storeId) return false;
    if(plan.vehPlates && plan.vehPlates.length > 0) {
        if((store.vehPlates===plan.vehPlates && store.comId===plan.comId) || !store.vehPlates) return true;
        return false;
    }
    return true;
}

function isGroupBindable(group, plan) {
    // 이미 TsrIssued면 불가능.
    if(plan.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) return false;
    // plan에 bindStores 있으면 애초에 불가능.
    if(plan.bindStores && plan.bindStores.length > 0) return false;
    // 같은 planId 있거나, 같은 점포가 하나라도 있으면 불가능.
    // stores 없으면 애초에 불가능.
    if(group.stores && group.stores.length > 0) {
        for(let i=0; i<group.stores.length; i++) {
            const store = group.stores[i];
            if(store.planId === plan.planId) {
                return false;
            }
            if(store.storeId === plan.storeId) {
                return false;
            }
        }
        // 이제 차량이 있으면, 같은 차량이 하나라도 있거나, 모두 차량이 null이어냐 가능.
        if(plan.vehPlates && plan.vehPlates.length > 0) {
            // 같은 차량이 하나라도 있거나 하나도 없는 경우 가능.
            const listWithVeh = group.stores.filter((store) => Boolean(store.vehPlates) && store.vehPlates.length > 0);
            if(listWithVeh.length > 0) {
                for(let i=0; i<listWithVeh.length; i++) {
                    const store = listWithVeh[i];
                    if(store.vehPlates === plan.vehPlates && store.comId === plan.comId) return true;
                }
            }
            else return true;
        }
        else return true; // 차량이 없으면 어떤 그룹에도 가능.
    }
    return false;
}

/**
 * 동적관리.
 * 운송요청에서 동적 버튼 클릭하면 나오는 팝업.
 * 동적생성, 동적에 추가, 동적에서 하나 제거, 동적 순서 변경.
 * @param {*} param0 
 * @returns 
 */
export default function TransPlanBindManager({
    open,
    onClose,
    defaultPlan,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [plan, setPlan] = useState({});
    const transPlanBindStoreListData = useReactiveVar(transPlanBindStoreListRepo);
    const transPlanBindStoreNotBoundListData = useReactiveVar(transPlanBindStoreNotBoundListRepo);
    const [filteredGroupList, setFilteredGroupList] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const [searchGroupText, setSearchGroupText] = useState('');
    const [storeDragging, setStoreDragging] = useState({});
    const [storeHovering, setStoreHovering] = useState({});
    const [filterSoloList, setFilterSoloList] = useState(true);
    const [filterBoundList, setFilterBoundList] = useState(true);
    const [wantToInsert, setWantToInsert] = useState({want:false, store: null}); // 기존 동적 그룹에 추가하고자 하는 "상태"
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const changeRef = useRef(false);

    const [getTransPlanBindStoreListBoundAndSolo, responseBoundAndSolo] = useLazyQuery(LIST_TRANS_PLAN_BIND_STORE_GROUP_AND_SOLO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onColpleteGetListBoundAndSolo(data, option)},
        onError: (error) => addAlert({title: '동적운송 정보 일괄 조회 오류', message: [error.message]})
    });
    const [getTransPlanTsrItem, responsePlanItem] = useLazyQuery(GET_TRANS_PLAN_TSR, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetPlanItem(data, option)},
		onError: (error) => addAlert({title: "운송요청 정보 조회 오류", message: [error.message]})
	});
    const [addTransPlanBindByJoining, responseJoinToBind] = useMutation( ADD_TRANS_PLAN_BIND, {
		onCompleted: (data, option) => onCompleteJoinToBind(data, option), 
		onError: (error) => addAlert({title: '새로운 동적 생성 오류', message: [error.message]})
	} );
    const [addTransPlanBindStore, responseAddTransPlanBindStore] = useMutation( ADD_TRANS_PLAN_BIND_STORE, {
        onCompleted: (data, option) => onCompleteAddTransPlanBindStore(data, option),
        onError: (error) => addAlert({title: '기존 동적그룹에 추가 오류', message: [error.message]})
    });
    const [editTransPlanBindStore, responseEdit] = useMutation( EDIT_TRANS_PLAN_BIND_STORE, {
		onCompleted: (data, option) => onCompleteEditTransPlanBindStore(data, option),
		onError: (error) => addAlert({title: TransPlanBindStoreDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeTransPlanBindStore, responseRemoveBindStore] = useMutation( REMOVE_TRANS_PLAN_BIND_STORE, {
		onCompleted: (data, option) => onCompleteRemoveStoreBound(data, option),
		onError: (error) => addAlert({title: TransPlanBindStoreDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getTransPlanBindStoreListBoundAndSoloNow = (planDateGiven) => {
        const planDate = planDateGiven || plan.planDate;
        if(planDate) {
            getTransPlanBindStoreListBoundAndSolo({variables: {dcCd:sessionInfo.user.dscIdAs, planDate:planDate}});
        }
    };

    useEffect(() => {
        if(open && defaultPlan) {
            setPlan(defaultPlan);
            //getTransPlanBindStoreListNotBoundNow(defaultPlan.planDate);
            //getTransPlanBindStoreListAtDateNow(defaultPlan.planDate);
            getTransPlanBindStoreListBoundAndSoloNow(defaultPlan.planDate);
            // 동적이 없으면(단독운송이면) 그룹에 필터링을 한다.
            setFilterBoundList(
                defaultPlan.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued &&
                !(defaultPlan.bindStores && defaultPlan.bindStores.length > 0));
        }
        else setPlan({});
    }, [open, defaultPlan]);

    // filter transPlanBindStoreListData using searchGroupText and set to filteredGroupList
    useEffect(() => {
        const tempList
        = filterBoundList
        ? transPlanBindStoreListData.records.filter((group) => isGroupBindable(group, plan))
        : transPlanBindStoreListData.records;

        if(searchGroupText && searchGroupText.length > 0) {
            const filtered = tempList.filter((group) => {
                //if(group.comName.includes(searchGroupText)) return true;
                if(group.vehPlates) {
                    if(group.vehPlates.includes(searchGroupText)) return true;
                }
                if(group.bindStores) {
                    for(let i=0; i<group.bindStores.length; i++) {
                        const store = group.bindStores[i];
                        if(store.vehPlates) {
                            if(store.vehPlates.includes(searchGroupText)) return true;
                        }
                        if(store.storeName.includes(searchGroupText)) return true;
                    }
                }
                return false;
            });
            setFilteredGroupList(filtered);
        }
        else setFilteredGroupList(tempList);
    }, [searchGroupText, transPlanBindStoreListData, plan, filterBoundList]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<

    const onColpleteGetListBoundAndSolo = (data, clientOption) => {
        if(data.transPlanBindStoreListNotBoundForSharing) {
            transPlanBindStoreNotBoundListRepo({
                when:new Date().getTime()/1000,
                records:data.transPlanBindStoreListNotBoundForSharing
            });
        }
        if(data.transPlanBindStoreListAtDate) {
            transPlanBindStoreListRepo({
                when: new Date().getTime()/1000,
                records: transPlanBindStoreListToTree(data.transPlanBindStoreListAtDate)
            });
        }
    };

    const onCompleteGetPlanItem = (data, option) => {
        if(data.transPlanTsrItem) {
            setPlan(data.transPlanTsrItem);

            setFilterBoundList(
                data.transPlanTsrItem.reqStateFlag !== TopCode.ReqStateFlag.TsrIssued &&
                !(data.transPlanTsrItem.bindStores && data.transPlanTsrItem.bindStores.length > 0));
        }
        else addAlert({title:"운송요청 정보 조회", message:['지정한 운송요청을 찾을 수 없습니다.']});
    };

    const onCompleteJoinToBind = (data, clientOption) => {
        if(data.transPlanBindAdd.ok) {
            changeRef.current = true;
            getTransPlanBindStoreListBoundAndSoloNow();
            getTransPlanTsrItem({variables:{planId:plan.planId}});
        }
        else addAlert({title:'신규 동적그룹 생성 실패', message:[data.transPlanBindAdd.message]});
    };

    const onCompleteAddTransPlanBindStore = (data, clientOption) => {
        if(data.transPlanBindStoreAdd.ok) {
            changeRef.current = true;
            getTransPlanBindStoreListBoundAndSoloNow();
            getTransPlanTsrItem({variables:{planId:plan.planId}});
        }
        else addAlert({title:'기존 동적그룹에 추가 실패', message:[data.transPlanBindStoreAdd.message]});
    };

    const onCompleteEditTransPlanBindStore = (data, clientOption) => {
        if(data.transPlanBindStoreEdit.ok) {
            changeRef.current = true;
            getTransPlanBindStoreListBoundAndSoloNow();
            getTransPlanTsrItem({variables:{planId:plan.planId}});
        }
        else addAlert({title:TransPlanBindStoreDictionary.errorTitle.Edit, message:[data.transPlanBindStoreEdit.message]});
    };

    const onCompleteRemoveStoreBound = (data, clientOption) => {
        if(data.transPlanBindStoreRemove.ok) {
            changeRef.current = true;
            getTransPlanBindStoreListBoundAndSoloNow();
            getTransPlanTsrItem({variables:{planId:plan.planId}});
        }
        else addAlert({title:TransPlanBindStoreDictionary.errorTitle.Remove, message:[data.transPlanBindStoreRemove.message]});
    };

    /* #################################### */
    const onClickClose = () => {
        onClose(changeRef.current);
        changeRef.current = false;
    };

    const clearDragAndDrop = () => {
        setStoreDragging({});
        setStoreHovering({});
    };

    const onStartDragging = (e, store) => {
        if(wantToInsert.want) {
            e.preventDefault();
            return;
        }
        setStoreDragging(store);
    };

    const onDraggingOverStore = (e, store) => {
        e.preventDefault();
        setStoreHovering(store);
    };

    const getIndexInGroup = (store) => {
        if(plan.bindStores && plan.bindStores.length > 0) {
            for(let i=0; i<plan.bindStores.length; i++) {
                if(plan.bindStores[i].planId === store.planId) return i;
            }
        }
        return -1;
    };

    const onDropOverStore = (e, store) => {
        e.preventDefault();
        // 바로 아래칸의 위로 들어갈 수 없다. 이미 거기 있으니까.
        const indexDrag = getIndexInGroup(storeDragging);
        const indexTarget = getIndexInGroup(store);
        if(indexDrag === indexTarget || indexTarget === indexDrag+1) {
            enqueueSnackbar('이동이 아닙니다.', {variant:AppStyle.mui.success, autoHideDuration:1000});
            return;
        }
        editTransPlanBindStore({
            variables: {
                transPlanBindStore: {
                    bindGroupNo: plan.bindStores[0].bindGroupNo,
                    planId: storeDragging.planId,
                    planIdAnchor: store.planId,
                    front: true
                }
            }
        });
        clearDragAndDrop();
    };

    const onDropAtLast = (e) => {
        // 맨 끝의 플랜은 맨끝으로 갈 수 없다.(이미 끝이니까)
        const indexDrag = getIndexInGroup(storeDragging);
        e.preventDefault();
        if(indexDrag === plan.bindStores.length-1) {
            enqueueSnackbar('이동이 아닙니다.', {variant:AppStyle.mui.success, autoHideDuration:1000});
            return;
        }
        editTransPlanBindStore({
            variables: {
                transPlanBindStore: {
                    bindGroupNo: plan.bindStores[0].bindGroupNo,
                    planId: storeDragging.planId,
                    planIdAnchor: plan.bindStores[plan.bindStores.length-1].planId,
                    front: false
                }
            }
        });
        clearDragAndDrop();
    };

    const isBound = plan.bindStores && plan.bindStores.length > 0;

    const confirmJoinToBind = async (row) => {
        const confirm = await openConfirmDialog({
            title: '동적그룹 생성',
            content: [
                `${plan.storeName}, ${plan.hocha}호차와 ${row.storeName}, ${row.hocha}호차를 묶어 동적그룹을 생성합니다.`,
                '계속하시겠습니까?'
            ]
        });
        if(confirm) {
            addTransPlanBindByJoining({
                variables: {transPlanBind:{planId:plan.planId, planIdTwo: row.planId}}
            });
        }
    };

    const onClickRemoveStoreFromGroup = async (store) => {
        const confirm = await openConfirmDialog({
            title: '동적그룹에서 제외',
            content: [
                `동적그룹에서 ${store.storeName}, ${store.hocha}호차를 제외합니다.`,
                plan.bindStores.length > 2 ?
                `제외 후에는 ${plan.bindStores.length-1}개의 점포가 남습니다.` :
                '그룹에 2개 이하인 경우 동적그룹 전체가 해체됩니다.',
                '운송요청 자체는 변경되지 않습니다',
                '계속하시겠습니까?'
            ]
        });
        if(confirm) {
            removeTransPlanBindStore({ variables: { transPlanBindStore: { planId: store.planId } } });
        }
    };

    // 우측 단독운송의 점포/호차 목록에서 선택한 점포를 선택된 운송요청이 속한 동적그룹에 추가.
    const confirmAddTransPlanBindStore = async (storeAncho, front) => {
        if(wantToInsert.want && plan.bindStores && plan.bindStores.length > 0) {
            const soloStore = wantToInsert.store;
            const confirm = await openConfirmDialog({
                title: '동적그룹에 추가',
                content: [
                    `${plan.storeName}, ${plan.hocha}호차 동적그룹에 ${soloStore.storeName}, ${soloStore.hocha}호차를 추가합니다.`,
                    '계속하시겠습니까?'
                ]
            });

            setWantToInsert({want:false, store:null});

            if(confirm) {
                addTransPlanBindStore({
                    variables: {
                        transPlanBindStore: {
                            bindGroupNo: plan.bindStores[0].bindGroupNo,
                            planId: soloStore.planId,
                            planIdAnchor: storeAncho ? storeAncho.planId : null,
                            front: front ? true : false,
                        }
                    }
                });
            }
        }
        setWantToInsert({want:false, store:null});
    };

    // 메인 운송요청을 좌하단의 동적운송 그룹에 추가.
    const confirmAddMainPlanToGroup = async (group, storeAncho, front) => {
        if(!plan.bindStores || plan.bindStores.length === 0) {
            const confirm = await openConfirmDialog({
                title: '동적그룹에 추가',
                content: [
                    `${plan.storeName}, ${plan.hocha}호차를 동적그룹에 추가합니다.`,
                    '계속하시겠습니까?'
                ]
            });

            if(confirm) {
                addTransPlanBindStore({
                    variables: {
                        transPlanBindStore: {
                            bindGroupNo: group.bindGroupNo,
                            planId: plan.planId,
                            planIdAnchor: storeAncho ? storeAncho.planId : null,
                            front: front ? true : false,
                        }
                    }
                });
            }
        }
    };

    const onClickSetMainPlan = (store) => {
        if(store.planId !== plan.planId)
            getTransPlanTsrItem({variables:{planId:store.planId}});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.JOIN: confirmJoinToBind(row); break;
            case AppObject.command.NEW: setWantToInsert({want:true, store: row}); break;
            //case AppObject.command.DELETE: confirmRemove(row); break;
            //case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };
    const toolsForSolo = []; // tools for solo


    const canJoinToBind = checkAuth(authTableTree.tree.pr_trans_plan_bind_add); // 두 운송의뢰를 하나의 동적으로 묶기
    const canAddTransPlanBindStore = checkAuth(authTableTree.tree.pr_trans_plan_bind_store_add); // 기존 동적 그룹에 새로운 운송의뢰 추가
    const canRemoveBindStore = checkAuth(authTableTree.tree.pr_trans_plan_bind_store_remove); // 기존 그룹에서 제외
    const canEditBindStore = checkAuth(authTableTree.tree.pr_trans_plan_bind_store_edit); // 순서 변경

    if(isBound) {
        if(canAddTransPlanBindStore) {
            toolsForSolo.push({
                id: AppObject.command.NEW, // add item to an existing group
                icon: <AddHomeIcon/>,
                color: AppStyle.mui.success,
                where: (row) => isBindable(row, plan),
                title: '현재 동적그룹에 추가'
            });
        }
    }
    else {
        if(canJoinToBind) {
            toolsForSolo.push({
                id: AppObject.command.JOIN, // join this item with the plan(not grouped) to make a group
                icon: <AddHomeIcon />,
                color: AppStyle.mui.warning,
                where: (row) => isBindable(row, plan),
                title: `${plan.storeName}, ${plan.hocha}호차와 묶어 동적그룹 생성`
            });
        }
    }
    const loadingAny
    = responseBoundAndSolo.loading
    || responsePlanItem.loading
    || responseAddTransPlanBindStore.loading // 기존 그룹에 추가
    || responseEdit.loading // 순서 변경
    || responseRemoveBindStore.loading // 기존 그룹에서 제외
    || responseJoinToBind.loading; // 2개의 단독운송을 묶어 동적그룹 생성

    /**
     * 현재 선택된 플랜의 동적 정보 중 하나의 점포,호차 정보 표시.
     * @param {*} store 
     * @returns 
     */
    const renderStoreRow = (store) => {
        return (
            <TableRow key={store.storeId}>
                <ValueCell
                >
                    <div style={{
                            display:'flex', alignItems:'center',
                            cursor: storeDragging.storeId ? 'grabbing' : 'grab',
                            backgroundColor:
                                storeHovering.storeId === store.storeId
                                ? '#eef' : AppStyle.mui.inherit
                        }}
                        draggable={!wantToInsert.want && canEditBindStore}
                        onDragStart={(e) => onStartDragging(e, store)}
                        onDragOver={(e) => onDraggingOverStore(e, store)}
                        onDrop={(e) => onDropOverStore(e, store)}
                    >
                        {
                            storeHovering.storeId === store.storeId
                            && storeDragging.storeId !== store.storeId
                            ?
                            <ArrowOutwardIcon
                                size={AppWord.SMALL}
                                color={AppStyle.mui.warning}
                                style={{marginRight:5}}
                            />
                            :
                            <StorefrontIcon
                                size={AppWord.SMALL}
                                color={
                                    store.storeId === plan.storeId
                                    ? AppStyle.mui.inherit
                                    : AppStyle.mui.warning
                                }
                                style={{marginRight:5}}
                            />
                        }
                        <span
                            style={{
                                color: storeDragging.storeId === store.storeId
                                ? 'red' : AppStyle.mui.inherit
                            }}
                        >
                            {store.storeName}
                        </span>
                    </div>
                </ValueCell>
                <ValueCell>{store.hocha}호</ValueCell>
                <ValueCell>{store.dptrReqTime ? getHhmm(store.dptrReqTime) : '----'}</ValueCell>
                <ValueCell style={{padding:2}}>
                    {
                        wantToInsert.want ?
                        <Tooltip title='이 점포의 앞에 동적추가'>
                            <BlinkingIconButton
                                size={AppWord.SMALL}
                                color={AppStyle.mui.warning}
                                onClick={() => confirmAddTransPlanBindStore(store, true)}
                            >
                                <NorthWestIcon />
                            </BlinkingIconButton>
                        </Tooltip>
                        :
                        <Tooltip title={store.storeName+" 점포를 제외합니다."}>
                            <IconButton
                                size={AppWord.SMALL}
                                color={AppStyle.mui.warning}
                                disabled={!canRemoveBindStore}
                                onClick={() => onClickRemoveStoreFromGroup(store)}
                            >
                                <RemoveCircleIcon />
                            </IconButton>
                        </Tooltip>
                    }
                </ValueCell>
            </TableRow>
        );
    };

    const renderMiscChip = (item) => {
        if(item) {
            if(item.reqStateFlag === TopCode.ReqStateFlag.TsrNotIssued) {
                return(
                    <Chip
                        label="TSR미발행"
                        size={AppWord.SMALL}
                        color={AppStyle.mui.warning}
                        style={{marginLeft: 5, fontSize:'0.7rem', fontWeight:'normal'}}
                    />
                );
            }
            if(item.reqStateFlag === TopCode.ReqStateFlag.TsrIssued) {
                return(
                    <Chip
                        label="TSR발행"
                        size={AppWord.SMALL}
                        color={AppStyle.mui.success}
                        style={{marginLeft: 5, fontSize:'0.7rem', fontWeight:'normal'}}
                    />
                )
            }
        }
        return null;
    };

    /**
     * 메인 배차 정보.
     * @returns 
     */
    const renderPlanBox = () => {
        return(
            <PlanBox>
                <PlanBoxHeader>
                    <Box style={{flexGrow:1}}>
                        운송요청 정보
                    </Box>
                    {
                        defaultPlan && defaultPlan.planId !== plan.planId &&
                        <Button
                            size={AppWord.SMALL}
                            variant={AppStyle.mui.contained}
                            style={{float:'right'}}
                            color={AppStyle.mui.success}
                            startIcon={<UndoIcon />}
                            onClick={() => onClickSetMainPlan(defaultPlan)}
                        >
                            {defaultPlan.storeName}, {defaultPlan.hocha}호차
                        </Button>
                    }
                </PlanBoxHeader>
                <PlanBoxBody>
                    <TableContainer>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TitleCell>
                                        점포
                                        <span style={{marginLeft:5, color: '#700'}}>
                                            [{plan.storeId}]
                                        </span>
                                    </TitleCell>
                                    <ValueCell>
                                        {plan.storeName}</ValueCell>
                                    <TitleCell>호차</TitleCell>
                                    <ValueCell>{plan.hocha}호차</ValueCell>
                                    <TitleCell>운송센터</TitleCell>
                                    <ValueCell>{plan.dcName}</ValueCell>
                                </TableRow>
                                <TableRow>
                                    <TitleCell>출발요청</TitleCell>
                                    <ValueCell>
                                        {getHhmm(plan.dptrReqTime)}
                                        {renderMiscChip(plan)}
                                    </ValueCell>
                                    <TitleCell>도착요청</TitleCell>
                                    <ValueCell>{getHhmm(plan.arrReqTime)}</ValueCell>
                                    <TitleCell>의뢰일자</TitleCell>
                                    <ValueCell>{plan.planDate}</ValueCell>
                                </TableRow>
                                <TableRow>
                                    <TitleCell>차량</TitleCell>
                                    <ValueCell>{plan.vehPlates || '미정'}</ValueCell>
                                    <TitleCell>운송사</TitleCell>
                                    <ValueCell colSpan={3}>{plan.comName}</ValueCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </PlanBoxBody>
            </PlanBox>
        )
    };

    /**
     * 현재 선택된 플랜의 동적 상태를 보여줌.
     * */
    const renderPlanBindBox = () => {
        return(
            <PlanBindBox>
                <PlanBoxHeader>
                    {
                        wantToInsert.want ?
                        <>
                            추가할 위치의 화살표 아이콘을 클릭하세요.
                            <Button
                                size={AppWord.SMALL}
                                variant={AppStyle.mui.contained}
                                onClick={()=>setWantToInsert({want:false, store:null})}
                                style={{marginLeft:10}}
                                color={AppStyle.mui.warning}
                            >
                                취소
                            </Button>
                        </>
                        :
                        <>
                        <ForwardIcon style={{marginRight:5}} size={AppWord.MEDIUM} />
                        <span>{plan.storeName}, {plan.hocha}호차 동적현황</span>
                        </>
                    }
                </PlanBoxHeader>
                <PlanBoxBody>
                    {
                        plan.bindStores && plan.bindStores.length > 0 &&
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TitleCell style={{padding:3}}>점포</TitleCell>
                                        <TitleCell style={{padding:3}}>호차</TitleCell>
                                        <TitleCell style={{padding:3}}>출발요청</TitleCell>
                                        <ValueCell style={{padding:0, textAlign:'center'}}>
                                            <BuildIcon
                                                style={{fontSize:'0.8rem', color:'#66f'}}
                                            />
                                        </ValueCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        plan.bindStores.map((store) => {
                                            return renderStoreRow(store);
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                    {
                        plan.bindStores && plan.bindStores.length > 0 ?
                        <Box style={{
                                fontSize: '0.9rem', padding: 5,
                                display: 'flex', alignItems: 'center',
                            }}
                        >
                            {
                                wantToInsert.want ?
                                <>
                                <BlinkingIconButton
                                    size={AppWord.SMALL}
                                    color={AppStyle.mui.warning}
                                    style={{marginRight:10}}
                                    onClick={() => confirmAddTransPlanBindStore(null, false)}
                                >
                                    <ArrowForwardIcon />
                                </BlinkingIconButton>
                                <span>맨 끝에 넣으려면 여기를 누르세요.</span>
                                </>
                                :
                                <TableContainer>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <ValueCell
                                                    style={{
                                                        color: storeDragging.storeId ? 'red' : '#66f'
                                                    }}
                                                    onDrop={(e) => onDropAtLast(e)}
                                                    onDragOver={(e) => e.preventDefault()}
                                                >
                                                    {
                                                        !canEditBindStore ?
                                                        <span>
                                                            순서변경 권한이 없습니다.
                                                        </span>
                                                        :
                                                        storeDragging.storeId ?
                                                        <span>
                                                            맨 끝으로 이동하려면 점포를 여기에 놓으세요.
                                                        </span>
                                                        :
                                                        <span>
                                                            마우스로 점포를 드래그하여 순서를 변경합니다.
                                                        </span>
                                                    }
                                                </ValueCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            }
                        </Box>
                        :
                        <Box style={{
                            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height: '100%'
                        }}>
                            <Box><b>단독운송 상태입니다.</b></Box>
                            <ul style={{fontSize:'0.85rem', paddingLeft: 5}}>
                                <li>좌측 하단의 단독운송 목록에서 선택하여 동적을 만들 수 있습니다.</li>
                                <li>우측 하단의 동적운송 목록에서 선택하여 추가할 수 있습니다.</li>
                                {
                                    plan.reqStateFlag === TopCode.ReqStateFlag.TsrIssued &&
                                    <li style={{color:'#900'}}>
                                        단, 이미 TSR이 발행된 경우에는 동적운송을 추가할 수 없습니다.
                                    </li>
                                }
                            </ul>
                        </Box>
                    }
                </PlanBoxBody>
            </PlanBindBox>
        );
    };

    /**
     * 동적 그룹 전체 표시.
     * @param {*} group 
     * @param {*} index 
     * @returns 
     */
    const renderBoundGroup = (group, index) => {
        const groupBindable = isGroupBindable(group, plan);
        return(
            <BoundGroupRecord key={group.bindGroupNo}>
                <BoundGroupVehicle>
                    <span style={{marginRight:10, color: '#66f', fontSize:'0.8rem'}}>[{index+1}]</span>
                    {group.vehPlates || '미정('+group.bindGroupNo+')'}, {group.comName}
                </BoundGroupVehicle>
                <BoundGroupStoreListBox>
                    {
                        group.stores.map((store) => {
                            return(
                                <BoundStoreRow key={store.storeId}
                                    onClick={() => onClickSetMainPlan(store)}
                                >
                                    <BoundStoreName>
                                        {
                                            groupBindable &&
                                            <Tooltip
                                                title="이 점포 앞에 추가하려면 이 아이콘을 클릭하세요."
                                            >
                                                <IconButton
                                                    size={AppWord.SMALL}
                                                    color={AppStyle.mui.warning}
                                                    onClick={(e)=>{
                                                        e.stopPropagation();
                                                        confirmAddMainPlanToGroup(group, store, true)}}
                                                >
                                                    <ArrowOutwardIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        <span style={{color:'#66f', marginRight:5}}>
                                            {store.storeId}:
                                        </span>
                                        {store.storeName}
                                    </BoundStoreName>
                                    <BoundStoreHocha>
                                        <Tooltip
                                            title={
                                                <ul>
                                                    <li>{store.storeId}</li>
                                                    <li>{store.comName}({store.comId})</li>
                                                    <li>{store.vehPlates}</li>
                                                </ul>
                                            }
                                        >
                                            <span>{store.hocha}호차</span>
                                        </Tooltip>
                                        
                                    </BoundStoreHocha>
                                    <BoundStoreDptrReqTime>{
                                        store.dptrReqTime
                                        ? getHhmm(store.dptrReqTime)
                                        : '----'
                                    } 출발요청
                                    </BoundStoreDptrReqTime>
                                    {/* <BoundStoreTools>
                                        <Tooltip title={store.storeName+" 점포를 제외합니다."}>
                                            <span>
                                                <IconButton
                                                    size={AppWord.SMALL}
                                                    color={AppStyle.mui.warning}
                                                >
                                                    <RemoveCircleIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </BoundStoreTools> */}
                                </BoundStoreRow>
                            );
                        })
                    }
                    {
                        groupBindable &&
                        <BoundStoreRow
                        >
                            <IconButton
                                size={AppWord.SMALL}
                                color={AppStyle.mui.warning}
                                onClick={(e)=>{
                                    e.stopPropagation();
                                    confirmAddMainPlanToGroup(group, null, false)}}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                            <span>
                                맨 아래에 추가하려면 이 아이콘을 클릭하세요.
                            </span>
                        </BoundStoreRow>
                    }
                </BoundGroupStoreListBox>
            </BoundGroupRecord>
        );
    };

    /**
     * 동적 차량-점포 트리. (좌측 하단)
     * 점포 클릭하면 메인 배차정보에 올라가 표시 됨.
     * @returns 
     */
    const renderBindTreeBox = () => {
        return(
            <BindTreeContainer>
                <ListHeader style={{backgroundColor: AppStyle.SecondaryRGB}}>
                    동적 - 점포를 선택해서 운송요청정보를 조회할 수 있습니다.
                </ListHeader>
                <ListControl>
                    <ControlBox>
                        <SearchBox value={searchGroupText} onChangeText={setSearchGroupText}
                            width={150}
                        />
                    </ControlBox>
                    <ToolBox>
                        {
                            loadingAny ?
                            <CircularProgress size={20} /> :
                            <CountLabel count={filteredGroupList.length} />
                        }
                        <VzmCheckbox
                            dict={{id:'filterBoundList', label: '동적가능항목'}}
                            value={filterBoundList}
                            onChange={(id,value) => setFilterBoundList(value)}
                            fontSize='0.9rem'
                            disabled={(plan.bindStores && plan.bindStores.length > 0) && !filterBoundList}
                        />
                    </ToolBox>
                </ListControl>
                <BindTreeBox>
                    <BindTreeWrapper>
                        {
                            filteredGroupList.map((group, index) => {
                                return renderBoundGroup(group, index);
                            })
                        }
                        {
                            filteredGroupList.length === 0 &&
                            <Box style={{
                                display:'flex',
                                flexDirection:'column',
                                justifyContent:'center',
                                alignItems:'center',
                                paddingTop:20
                            }}>
                                <img src={StoreHouseSvg} alt="_" height="150" />
                                <div style={{marginTop:20, fontSize:'1.2rem', color:'#fa3'}}>
                                    조건에 맞는 동적그룹이 없습니다.
                                </div>
                            </Box>
                        }
                    </BindTreeWrapper>
                </BindTreeBox>
            </BindTreeContainer>
        );
    };

    /**
     * 단독운송 점포,호차 목록을 보여줌. (우측 하단)
     * 점포 클릭하면 메인 배차정보에 올라가 표시 됨.
     * @returns 
     */
    const renderSoloListBox = () => {
        return(
            <SoloListContainer>
                <ListHeader>
                    <ControlBox>
                        단독 운송 - 점포를 선택해서 운송요청정보를 조회할 수 있습니다.
                    </ControlBox>
                </ListHeader>
                <ListControl>
                    <ControlBox>
                        <SearchBox value={searchText} onChangeText={setSearchText}
                            width={150}
                        />
                    </ControlBox>
                    <ToolBox>
                        {
                            loadingAny ?
                            <CircularProgress size={20} /> :
                            <CountLabel count={countToDisplay} />
                        }
                        <VzmCheckbox
                            dict={{id:'filterSoloList', label: '동적가능항목'}}
                            value={filterSoloList}
                            onChange={(id,value) => setFilterSoloList(value)}
                            fontSize='0.9rem'
                        />
                    </ToolBox>
                </ListControl>
                <FlexyTable
                    name={TransPlanBindStoreDictionary.tableProps.tableId}
                    tableProps={TransPlanBindStoreDictionary.tableProps}
                    records={
                        filterSoloList ?
                        transPlanBindStoreNotBoundListData.records.filter((store) => {
                            return isBindable(store, plan);
                        }) :
                        transPlanBindStoreNotBoundListData.records.filter((store) => {
                            return store.storeId !== plan.storeId && store.planId !== plan.planId;
                        })
                    }
                    search={searchText}
                    tools={toolsForSolo}
                    onClickTool={onClickToolOnRow}
                    onChangeCountToDisplay={setCountToDisplay}
                    onRowClicked={onClickSetMainPlan}
                />
            </SoloListContainer>
        );
    };

    return(
        <DraggableEditor
            open={open}
            title="운송요청 동적 관리"
            maxWidth="xl"
            startIcon={<HubIcon fontSize={AppWord.LARGE}/>}
        >
            <DialogContent style={{height: '70vh', minWidth: '50vw'}}>
                <BigContainer
                    onMouseLeave={(e)=>{if(storeDragging.storeId) clearDragAndDrop()}}
                    onDrop={(e)=>{if(storeDragging.storeId) clearDragAndDrop()}}
                >
                    <PlanAndSoloList>
                        {renderPlanBox()}
                        {renderBindTreeBox()}
                    </PlanAndSoloList>
                    <BindArea>
                        {renderPlanBindBox()}
                        {renderSoloListBox()}
                    </BindArea>
                </BigContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClickClose} color={AppStyle.mui.warning}
                    variant={AppStyle.mui.contained}
                    disabled={loadingAny}
                    startIcon={<ReplyIcon />}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}